/**
 * @file     AddressAutocomplete.tsx
 * @purpose  Address autocomplete backed by Photon (komoot) — free OSM-based
 *           geocoding, no key, no quota. Replaces Google Places (removed
 *           2026-06-01 after billing incident). Preserves a typed strata unit
 *           because Photon returns the civic building, not the unit.
 */
"use client";

import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import type { TextFieldProps } from "@mui/material/TextField";
import { useTranslation } from 'react-i18next';
import { mergeTypedUnit, splitUnitFromAddress } from '@/services/home-read/formatAddress';

export type StructuredAddress = {
  fullAddress: string;
  street: string;
  /** Strata unit the user typed, or null when the line is civic-only. */
  unit: string | null;
  city: string;
  province: string;
  postalCode: string;
  country: string;
};

type AddressAutocompleteProps = {
  value: string;
  onChange: (value: string) => void;
  onSelect?: (address: StructuredAddress) => void;
  /** ISO 3166-1 alpha-2 country codes to bias results. Defaults to ['ca']. */
  countries?: string[];
  /** Optional action rendered inside the trailing edge of the address field. */
  endAction?: ReactNode;
  textFieldProps?: Omit<TextFieldProps, "value" | "onChange">;
};

type PhotonFeature = {
  geometry?: { coordinates?: [number, number] };
  properties?: {
    name?: string;
    street?: string;
    housenumber?: string;
    city?: string;
    state?: string;
    country?: string;
    countrycode?: string;
    postcode?: string;
    osm_id?: number | string;
    osm_type?: string;
  };
};

const PHOTON_URL = 'https://photon.komoot.io/api/';

type Suggestion = { id: string; label: string; feature: PhotonFeature };

function civicStreet(feature: PhotonFeature): string {
  const p = feature.properties ?? {};
  const street = [p.housenumber, p.street].filter(Boolean).join(' ');
  // p.name is a last resort for the street line, but for some POIs it is a
  // comma-joined label; keep only its first segment so a whole address never
  // lands in the street field.
  const nameStreet = (p.name ?? '').split(',')[0]!.trim();
  return street || nameStreet;
}

function toStructured(feature: PhotonFeature, typedInput: string): StructuredAddress {
  const p = feature.properties ?? {};
  const street = civicStreet(feature);
  const civicFull = [street, p.city, p.state, p.postcode, p.country]
    .filter(Boolean)
    .join(', ');
  const unit = splitUnitFromAddress(typedInput).unit;
  return {
    fullAddress: mergeTypedUnit(typedInput, civicFull),
    street,
    unit,
    city: p.city ?? '',
    province: p.state ?? '',
    postalCode: p.postcode ?? '',
    country: p.country ?? '',
  };
}

function toLabel(feature: PhotonFeature, typedInput: string): string {
  const p = feature.properties ?? {};
  const street = civicStreet(feature) || p.name || '';
  const civic = [street, p.city, p.state].filter(Boolean).join(', ');
  return mergeTypedUnit(typedInput, civic);
}

export const AddressAutocomplete = ({
  value,
  onChange,
  onSelect,
  countries = ["ca"],
  endAction,
  textFieldProps,
}: AddressAutocompleteProps) => {
  const { t } = useTranslation();
  const [options, setOptions] = useState<Suggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Photon has no unit. Keep the last typed line so a suggestion click can
  // re-attach "4609" after the civic building comes back.
  const typedRef = useRef(value);
  typedRef.current = value;

  const fetchSuggestions = useCallback(async (input: string) => {
    const civicQuery = splitUnitFromAddress(input).street.trim() || input.trim();
    if (civicQuery.length < 3) {
      setOptions([]);
      return;
    }
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    setLoading(true);
    try {
      // Search the civic building only. "4609-1480 Howe" otherwise geocodes as
      // civic 4609, a different property, then overwrites the unit on select.
      const url = `${PHOTON_URL}?q=${encodeURIComponent(civicQuery)}&limit=6&lang=en`;
      const res = await fetch(url, { signal: ac.signal });
      if (!res.ok) throw new Error(`photon ${res.status}`);
      const body = (await res.json()) as { features?: PhotonFeature[] };
      const features = (body.features ?? []).filter((f) => {
        const cc = f.properties?.countrycode?.toLowerCase();
        return !cc || countries.length === 0 || countries.includes(cc);
      });
      setOptions(
        features.map((f, i) => ({
          id: `${f.properties?.osm_type ?? 'p'}/${f.properties?.osm_id ?? i}`,
          label: toLabel(f, input),
          feature: f,
        })),
      );
    } catch (err) {
      if ((err as { name?: string }).name !== 'AbortError') setOptions([]);
    } finally {
      setLoading(false);
    }
  }, [countries]);

  const handleInputChange = useCallback(
    (_event: React.SyntheticEvent, newValue: string, reason: string) => {
      // Selection is owned by handleSelect. MUI also fires this with 'reset'
      // / 'selectOption' to sync the input to the option label; honouring
      // those would clobber a typed unit ("4609-1480 Howe") and re-query
      // Photon with the civic building line.
      if (reason === 'clear') {
        typedRef.current = newValue;
        onChange(newValue);
        setOptions([]);
        return;
      }
      if (reason !== 'input') return;
      typedRef.current = newValue;
      onChange(newValue);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => fetchSuggestions(newValue), 250);
    },
    [onChange, fetchSuggestions],
  );

  const handleSelect = useCallback(
    (_event: React.SyntheticEvent, selected: string | Suggestion | null) => {
      if (!selected) return;
      if (typeof selected === 'string') {
        onChange(selected);
        return;
      }
      const typed = typedRef.current;
      const structured = toStructured(selected.feature, typed);
      // When the caller collects the parts separately, let it decide what the
      // visible line shows (typically just the street). Only fall back to the
      // full label when there is no onSelect to place them.
      if (onSelect) onSelect(structured);
      else onChange(mergeTypedUnit(typed, selected.label));
    },
    [onChange, onSelect],
  );

  useEffect(() => () => abortRef.current?.abort(), []);

  return (
    <Autocomplete
      freeSolo
      options={options}
      loading={loading}
      loadingText={t('addressAutocomplete.loading', { defaultValue: 'Searching...' })}
      inputValue={value}
      onInputChange={handleInputChange}
      onChange={handleSelect}
      getOptionLabel={(option) => (typeof option === 'string' ? option : option.label)}
      getOptionKey={(option) => (typeof option === 'string' ? option : option.id)}
      isOptionEqualToValue={(opt, val) => typeof opt !== 'string' && typeof val !== 'string' && opt.id === val.id}
      filterOptions={(x) => x}
      renderInput={(params) => (
        <TextField
          {...params}
          {...textFieldProps}
          autoComplete="street-address"
          InputProps={{
            ...params.InputProps,
            ...textFieldProps?.InputProps,
            endAdornment: (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {endAction}
                {textFieldProps?.InputProps?.endAdornment ?? params.InputProps.endAdornment}
              </Box>
            ),
          }}
        />
      )}
    />
  );
};
